<script lang="ts">
  import { credentialStatus, renewCredential, type BadgeAward } from './api';
  let { award, onupdated }: { award: BadgeAward; onupdated: () => Promise<void> } = $props();
  let recipient = $state('');
  $effect(() => { if (!recipient) recipient = award.recipients[0] ?? ''; });
  let expires = $state('');
  let busy = $state(false);
  let error = $state('');
  let notice = $state('');
  let history = $state<Array<{ expires_at: string; recorded_at: string; applied: boolean }> | null>(null);
  // Retain the same update ID when retrying an uncertain network response.
  let pending: { value: string; recipient: string; id: string } | null = null;
  const current = $derived(award.validity?.[recipient]?.expires_at);
  async function loadHistory() {
    error = '';
    try {
      const response = await fetch(`/api/organization/awards/${award.event_id}/credentials/${recipient}/history`);
      if (!response.ok) throw new Error('Could not load renewal history.');
      history = (await response.json()).renewals;
    } catch (cause) { error = cause instanceof Error ? cause.message : 'Could not load history.'; }
  }
  $effect(() => { void recipient; history = null; });
  async function save(event: SubmitEvent) {
    event.preventDefault(); error = ''; notice = ''; busy = true;
    try {
      const value = new Date(expires).toISOString();
      if (current && new Date(value) <= new Date(current)) throw new Error('Choose a date later than the current expiry.');
      if (new Date(value) <= new Date(award.created_at)) throw new Error('Expiry must be after the original issue date.');
      if (!pending || pending.value !== value || pending.recipient !== recipient) pending = { value, recipient, id: crypto.randomUUID() };
      await renewCredential(award.event_id, recipient, value, pending.id);
      await onupdated();
      notice = 'Expiry saved. The credential ID and verification link are unchanged.';
      pending = null; history = null;
    } catch (cause) { error = cause instanceof Error ? cause.message : 'Could not save expiry.'; }
    finally { busy = false; }
  }
</script>

<details class="expiry">
  <summary>Membership expiry</summary>
  <form onsubmit={save}>
    {#if award.recipients.length > 1}<label>Recipient<select bind:value={recipient} disabled={busy}>{#each award.recipients as key}<option value={key}>{key}</option>{/each}</select></label>{/if}
    <p>{current ? `Valid until ${new Date(current).toLocaleString()} · ${credentialStatus(award,recipient)}` : 'This credential has no expiry date.'}</p>
    <p>Set the paid-through date. Renewals extend this same credential. Cancelling auto-renew does not end the paid period.</p>
    <label>{current ? 'Extend validity until' : 'Valid until'} (your local time)<input type="datetime-local" bind:value={expires} required disabled={busy || Boolean(award.revocation)} /></label>
    <button type="submit" disabled={busy || Boolean(award.revocation)}>{busy ? 'Saving…' : 'Save expiry'}</button>
    {#if award.revocation}<p>This credential was revoked and cannot be renewed.</p>{/if}
    <button type="button" onclick={loadHistory}>View renewal history</button>
    {#if history}<ul aria-label="Renewal history">{#each history as entry}<li>{new Date(entry.recorded_at).toLocaleString()}: valid until {new Date(entry.expires_at).toLocaleString()}{entry.applied ? '' : ' · Already covered by a later expiry'}</li>{:else}<li>No expiry updates yet.</li>{/each}</ul>{/if}
    {#if error}<p role="alert">{error}</p>{/if}
    {#if notice}<p role="status">{notice}</p>{/if}
  </form>
</details>

<style>
  .expiry { grid-column: 1 / -1; width: 100%; padding: 12px 0; }
  summary { cursor: pointer; font-weight: 700; }
  form { display: grid; gap: 12px; margin-top: 12px; max-width: 520px; }
  p { margin: 0; font-size: 13px; line-height: 1.5; }
  label { display: grid; gap: 6px; font-size: 13px; font-weight: 700; }
  input, select, button { padding: 10px; border: 1px solid var(--line-strong); background: var(--surface); color: var(--ink); font: inherit; max-width: 100%; min-width: 0; }
  button { cursor: pointer; justify-self: start; }
  [role=alert] { color: #842f38; }
</style>
